<template>
  <BaseModal :isVisible="isVisible" @close="$emit('close')" width="6xl">
    <template #header>{{ title }}</template>
    <template #content>
      <div class="flex items-center justify-between mb-5">
        <p class="text-sm text-gray-500">
          {{ selectedCount }} of {{ availableCount }} columns selected
        </p>
        <div class="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-medium rounded-l-md border"
            :class="preset === 'essential'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
            @click="applyPreset('essential')"
          >
            Essential
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-medium border-t border-b"
            :class="preset === 'defaults'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
            @click="applyPreset('defaults')"
          >
            Defaults
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-medium rounded-r-md border"
            :class="preset === 'all'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
            @click="applyPreset('all')"
          >
            All
          </button>
        </div>
      </div>

      <div class="max-h-[60vh] overflow-y-auto space-y-6 pr-1">
        <div v-for="group in visibleGroups" :key="group.id">
          <button
            type="button"
            class="flex items-center w-full text-left mb-2"
            @click="toggleGroupCollapse(group.id)"
          >
            <svg
              class="w-4 h-4 mr-1.5 text-gray-500 transition-transform"
              :class="{ '-rotate-90': collapsed[group.id] }"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
            <h4 class="text-sm font-semibold text-gray-800">{{ group.label }}</h4>
            <span class="ml-2 text-xs text-gray-400">
              ({{ groupSelectedCount(group) }}/{{ groupAvailableCount(group) }})
            </span>
          </button>

          <div v-show="!collapsed[group.id]">
            <template v-if="group.sections">
              <div
                v-for="section in visibleSections(group)"
                :key="section.id"
                class="mb-3 last:mb-0"
              >
                <h5 class="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 px-1">
                  {{ section.label }}
                </h5>
                <div class="max-h-48 overflow-y-auto border border-gray-100 rounded-md bg-gray-50">
                  <table class="w-full text-left border-collapse">
                    <thead class="sticky top-0 bg-gray-100 text-xs text-gray-600 z-10">
                      <tr>
                        <th class="w-10 px-3 py-1.5 font-medium">Export</th>
                        <th class="w-56 px-3 py-1.5 font-medium">Variable</th>
                        <th class="px-3 py-1.5 font-medium">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="col in availableColumnsIn(section.columns)"
                        :key="col"
                        class="border-t border-gray-100 text-xs align-top"
                      >
                        <td class="px-3 py-1.5">
                          <input
                            type="checkbox"
                            :checked="columnMap[col]"
                            :disabled="lockedColumnSet.has(col)"
                            @change="toggleColumn(col, $event.target.checked)"
                          >
                        </td>
                        <td class="px-3 py-1.5 font-mono text-gray-800" :class="{ 'text-gray-400': lockedColumnSet.has(col) }">
                          {{ col }}
                        </td>
                        <td class="px-3 py-1.5 text-gray-600 leading-relaxed">
                          {{ describe(col) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="max-h-64 overflow-y-auto border border-gray-100 rounded-md bg-gray-50">
                <table class="w-full text-left border-collapse">
                  <thead class="sticky top-0 bg-gray-100 text-xs text-gray-600 z-10">
                    <tr>
                      <th class="w-10 px-3 py-1.5 font-medium">Export</th>
                      <th class="w-56 px-3 py-1.5 font-medium">Variable</th>
                      <th class="px-3 py-1.5 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="col in availableColumnsIn(group.columns)"
                      :key="col"
                      class="border-t border-gray-100 text-xs align-top"
                    >
                      <td class="px-3 py-1.5">
                        <input
                          type="checkbox"
                          :checked="columnMap[col]"
                          :disabled="lockedColumnSet.has(col)"
                          @change="toggleColumn(col, $event.target.checked)"
                        >
                      </td>
                      <td class="px-3 py-1.5 font-mono text-gray-800" :class="{ 'text-gray-400': lockedColumnSet.has(col) }">
                        {{ col }}
                      </td>
                      <td class="px-3 py-1.5 text-gray-600 leading-relaxed">
                        {{ describe(col) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end space-x-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          @click="downloadSelected"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Download
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import * as XLSX from 'xlsx'
import BaseModal from './BaseModal.vue'

function columnsInGroup(group) {
  if (group.sections) return group.sections.flatMap((s) => s.columns)
  return group.columns || []
}

export default {
  name: 'ColumnSelectionModal',
  components: { BaseModal },
  props: {
    isVisible: { type: Boolean, default: false },
    title: { type: String, default: 'Select Columns' },
    data: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    descriptions: { type: Object, default: () => ({}) },
    lockedColumns: { type: Array, default: () => [] },
    presets: {
      type: Object,
      default: () => ({
        essential: () => ({}),
        defaults: () => ({}),
        all: () => ({})
      })
    },
    sheetName: { type: String, default: 'Data' },
    filename: { type: String, default: 'export.xlsx' },
    extraSheets: { type: Array, default: () => [] }
  },
  emits: ['close'],
  data() {
    return {
      columnMap: {},
      preset: 'defaults',
      collapsed: {}
    }
  },
  computed: {
    dataColumns() {
      if (!this.data?.length) return []
      return Object.keys(this.data[0])
    },
    dataColumnSet() {
      return new Set(this.dataColumns)
    },
    lockedColumnSet() {
      return new Set(this.lockedColumns)
    },
    visibleGroups() {
      return this.groups.filter((g) => {
        return columnsInGroup(g).some((c) => this.dataColumnSet.has(c))
      })
    },
    selectedCount() {
      return this.dataColumns.filter((c) => this.columnMap[c]).length
    },
    availableCount() {
      return this.dataColumns.length
    }
  },
  watch: {
    isVisible(open) {
      if (open) this.applyPreset('defaults')
    }
  },
  methods: {
    availableColumnsIn(columns) {
      return columns.filter((c) => this.dataColumnSet.has(c))
    },
    visibleSections(group) {
      return (group.sections || []).filter((s) =>
        s.columns.some((c) => this.dataColumnSet.has(c))
      )
    },
    describe(col) {
      return this.descriptions[col] || col
    },
    toggleGroupCollapse(groupId) {
      this.collapsed = { ...this.collapsed, [groupId]: !this.collapsed[groupId] }
    },
    groupSelectedCount(group) {
      return columnsInGroup(group)
        .filter((c) => this.dataColumnSet.has(c) && this.columnMap[c])
        .length
    },
    groupAvailableCount(group) {
      return columnsInGroup(group)
        .filter((c) => this.dataColumnSet.has(c))
        .length
    },
    applyPreset(name) {
      const builder = this.presets[name]
      const base = builder ? builder() : {}
      const map = {}
      for (const col of this.dataColumns) {
        map[col] = col in base ? Boolean(base[col]) : (name === 'all')
      }
      this.columnMap = map
      this.preset = name
    },
    toggleColumn(col, checked) {
      if (this.lockedColumnSet.has(col)) return
      this.columnMap = { ...this.columnMap, [col]: checked }
      this.preset = null
    },
    downloadSelected() {
      const selected = this.dataColumns.filter((c) => this.columnMap[c])
      if (selected.length === 0) return

      const filtered = this.data.map((row) => {
        const out = {}
        for (const col of selected) {
          out[col] = row[col]
        }
        return out
      })

      const workbook = XLSX.utils.book_new()

      for (const extra of this.extraSheets) {
        XLSX.utils.book_append_sheet(workbook, extra.worksheet, extra.name)
      }

      const worksheet = XLSX.utils.json_to_sheet(filtered, { header: selected })
      XLSX.utils.book_append_sheet(workbook, worksheet, this.sheetName)

      XLSX.writeFile(workbook, this.filename)
      this.$emit('close')
    }
  }
}
</script>
